/**
 * @format
 */

import React from 'react';
import { Table } from 'antd';
import { useSelector } from 'react-redux';
import { AgentLeaderboardItem } from '../types/leaderboard.types';
import { getRankClass } from 'utils/getRankClass';

interface LeaderboardTableProps {
  data: AgentLeaderboardItem[];
  loading: boolean;
  error?: any;
}

const LeaderboardTable: React.FC<LeaderboardTableProps> = ({
  data,
  loading,
  error,
}) => {
  const userRole = useSelector((state: any) => state?.user?.userData?.role);
  const getAgentName = (agent: AgentLeaderboardItem) => {
    if (!agent.user) {
      return 'Unknown Agent';
    }
    const { firstName, lastName } = agent.user;
    return `${firstName || ''} ${lastName || ''}`.trim() || 'Unknown Agent';
  };

  const rate = (total: number, fraction: number) => {
    if (!total) return 0;
    return Math.round((fraction / total) * 100);
  };

  const cpa = (totalCost: number, totalSales: number) => {
    if (!totalSales) return 'N/A';
    return Math.round(totalCost / totalSales);
  };

  const isAgent = userRole === 'agent';

  const columns: any[] = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      render: (_: any, __: any, index: number) => (
        <span className={`table-rank ${getRankClass(index)}`}>{index + 1}</span>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (_: any, record: AgentLeaderboardItem) => getAgentName(record),
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        getAgentName(a).localeCompare(getAgentName(b)),
    },
    {
      title: 'Bill Rate',
      dataIndex: 'billRate',
      key: 'billRate',
      render: (_: any, record: AgentLeaderboardItem) =>
        `${rate(record.totalCalls, record.totalBillable)}%`,
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        rate(a.totalCalls, a.totalBillable) - rate(b.totalCalls, b.totalBillable),
    },
    {
      title: 'CPA-A',
      dataIndex: 'cpaA',
      key: 'cpaA',
      render: (_: any, record: AgentLeaderboardItem) => {
        const cpaValue = cpa(record.totalCost, record.totalPrivateMed);
        const cpaNum = typeof cpaValue === 'number' ? cpaValue : parseInt(cpaValue);
        return (
          <span style={{ color: cpaNum < 300 ? '#A1E3CB' : '#FF000094' }}>
            ${cpaValue}
          </span>
        );
      },
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        (typeof cpa(a.totalCost, a.totalPrivateMed) === 'number'
          ? (cpa(a.totalCost, a.totalPrivateMed) as number)
          : 0) -
        (typeof cpa(b.totalCost, b.totalPrivateMed) === 'number'
          ? (cpa(b.totalCost, b.totalPrivateMed) as number)
          : 0),
    },
    {
      title: 'CPA-B',
      dataIndex: 'cpaB',
      key: 'cpaB',
      render: (_: any, record: AgentLeaderboardItem) => {
        const cpaValue = cpa(
          record.totalCost,
          record.totalPrivateMed + record.totalMajorMed
        );
        const cpaNum = typeof cpaValue === 'number' ? cpaValue : parseInt(cpaValue);
        return (
          <span style={{ color: cpaNum < 100 ? '#A1E3CB' : '#FF000094' }}>
            ${cpaValue}
          </span>
        );
      },
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        (typeof cpa(a.totalCost, a.totalPrivateMed + a.totalMajorMed) === 'number'
          ? (cpa(a.totalCost, a.totalPrivateMed + a.totalMajorMed) as number)
          : 0) -
        (typeof cpa(b.totalCost, b.totalPrivateMed + b.totalMajorMed) === 'number'
          ? (cpa(b.totalCost, b.totalPrivateMed + b.totalMajorMed) as number)
          : 0),
    },
    {
      title: 'Spend',
      dataIndex: 'spend',
      key: 'spend',
      render: (_: any, record: AgentLeaderboardItem) =>
        `$${record.totalCost.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        a.totalCost - b.totalCost,
    },
    {
      title: 'ACA',
      dataIndex: 'totalMajorMed',
      key: 'totalMajorMed',
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        a.totalMajorMed - b.totalMajorMed,
    },
    {
      title: 'U65',
      dataIndex: 'totalPrivateMed',
      key: 'totalPrivateMed',
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        a.totalPrivateMed - b.totalPrivateMed,
    },
    {
      title: 'Q Calls',
      dataIndex: 'totalBillable',
      key: 'totalBillable',
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        a.totalBillable - b.totalBillable,
    },
    {
      title: 'Total Calls',
      dataIndex: 'totalCalls',
      key: 'totalCalls',
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        a.totalCalls - b.totalCalls,
    },
    {
      title: 'Premium',
      dataIndex: 'premium',
      key: 'premium',
      render: (_: any, record: AgentLeaderboardItem) => {
        const totalPremium =
          record.totalAncillaryPremium + record.totalPrivateMedPremium;
        return (
          <span>
            ${totalPremium.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        );
      },
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        a.totalAncillaryPremium +
        a.totalPrivateMedPremium -
        (b.totalAncillaryPremium + b.totalPrivateMedPremium),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'CR',
      dataIndex: 'conversionRate',
      key: 'conversionRate',
      render: (_: any, record: AgentLeaderboardItem) => {
        const conversion = rate(
          record.totalBillable,
          record.totalPrivateMed + record.totalMajorMed
        );
        return (
          <span style={{ color: conversion > 60 ? '#A1E3CB' : '#FF000094' }}>
            {conversion}%
          </span>
        );
      },
      sorter: (a: AgentLeaderboardItem, b: AgentLeaderboardItem) =>
        rate(a.totalBillable, a.totalPrivateMed + a.totalMajorMed) -
        rate(b.totalBillable, b.totalPrivateMed + b.totalMajorMed),
    },
  ];

  // Filter columns based on user role
  const filteredColumns = isAgent
    ? columns.filter(
        (col) =>
          !['cpaA', 'cpaB', 'billRate', 'spend', 'totalBillable', 'totalCalls'].includes(
            col.key
          )
      )
    : columns;

  return (
    <div style={{ width: '100%' }}>
      <Table
        columns={filteredColumns}
        dataSource={data}
        rowKey={(record) => record.userId}
        loading={loading}
        pagination={false}
        bordered
      />
      {error && <p style={{ color: 'red' }}>Error: {error.message}</p>}
    </div>
  );
};

export default LeaderboardTable;
